import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faTrashAlt } from "@fortawesome/free-solid-svg-icons";

import api from "../../api/http";
import "../../styles/admin/UsersMana.css";

function UsersManager() {
  const [users, setUsers] = useState([]);
  const [profilesByUserId, setProfilesByUserId] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const token = sessionStorage.getItem("access");
  const isAuthenticated = Boolean(token);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => mountedRef.current && setMessage(""), 5000);
    return () => clearTimeout(t);
  }, [message]);

  const parseApiError = useCallback((err) => {
    const d = err?.response?.data;
    if (!d) return err?.message || "A apărut o eroare.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d[0];
    if (typeof d.detail === "string") return d.detail;
    const k = Object.keys(d)[0];
    return Array.isArray(d[k]) ? d[k][0] : String(d[k]);
  }, []);

  const makeAvatarUrl = useCallback((avatar) => {
    if (!avatar) return "";
    if (avatar.startsWith("http")) return avatar;
    const base = api?.defaults?.baseURL || "";
    return base.replace(/\/$/, "") + "/" + avatar.replace(/^\//, "");
  }, []);

  const displayName = useCallback((p) => {
    const full = `${p?.first_name || ""} ${p?.last_name || ""}`.trim();
    return full || p?.user?.username || "Unknown User";
  }, []);

  const fetchMyProfile = async (signal) => {
    try {
      const r = await api.get("/users/profiles/me/", { signal });
      setIsSuperUser(Boolean(r?.data?.user?.is_superuser));
    } catch {
      setIsSuperUser(false);
    }
  };

  const fetchUsers = async (signal) => {
    const r = await api.get("/users/", { signal });
    return Array.isArray(r.data) ? r.data : r.data?.results || [];
  };

  const fetchProfilesAdmin = async (signal) => {
    const r = await api.get("/users/profiles/admin/", { signal });
    const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
    const map = {};
    list.forEach(p => p?.user?.id && (map[p.user.id] = p));
    return map;
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    const c = new AbortController();
    try {
      await fetchMyProfile(c.signal);
      const [u, p] = await Promise.all([
        fetchUsers(c.signal),
        fetchProfilesAdmin(c.signal),
      ]);
      if (!mountedRef.current) return;
      setUsers(u);
      setProfilesByUserId(p);
    } catch (e) {
      setError(parseApiError(e));
    } finally {
      mountedRef.current && setLoading(false);
    }
    return () => c.abort();
  }, [parseApiError]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const clean = loadAll();
    return () => typeof clean === "function" && clean();
  }, [isAuthenticated, loadAll]);

  const requireSuperUser = () => {
    if (isSuperUser) return true;
    setError("Permisiune respinsă: nu ești superuser.");
    return false;
  };

  // 🔁 ACTIVARE / DEZACTIVARE SUPERUSER
  const handleToggleSuperuser = async (userId, nextValue) => {
    if (!requireSuperUser()) return;

    const ok = window.confirm(
      nextValue
        ? "Sigur vrei să activezi superuser?"
        : "Sigur vrei să dezactivezi superuser?"
    );
    if (!ok) return;

    try {
      await api.patch(`/users/${userId}`, { is_superuser: nextValue });
      setUsers((prev) =>
        prev.map((u) => u.id === userId ? { ...u, is_superuser: nextValue } : u)
      );
      setMessage("Utilizator actualizat.");
    } catch (e) {
      setError(parseApiError(e));
    }
  };

  // 🗑️ ȘTERGERE USER
  const handleDeleteUser = async (userId) => {
    if (!requireSuperUser()) return;
    if (!window.confirm("Sigur vrei să ștergi acest utilizator?")) return;

    try {
      await api.delete(`/users/${userId}`);
      setUsers((p) => p.filter((u) => u.id !== userId));
      setMessage("Utilizator șters.");
    } catch (e) {
      setError(parseApiError(e));
    }
  };

  const renderedUsers = useMemo(() => [...users].reverse(), [users]);

  if (!isAuthenticated) return <div className="um-container">Nu ești autentificat.</div>;
  if (loading) return <div className="um-container">Loading...</div>;

  return (
    <div className="um-container">
      {message && <p className="um-msg um-msg--ok">{message}</p>}
      {error && <p className="um-msg um-msg--err">{error}</p>}

      <ul className="um-list">
        {renderedUsers.map((user) => {
          const profile = profilesByUserId[user.id];
          const name = profile ? displayName(profile) : user.username;
          const avatar = makeAvatarUrl(profile?.avatar);

          return (
            <li key={user.id} className="um-item">
              <div className="um-info">
                {avatar ? (
                  <img src={avatar} alt={name} className="um-avatar" />
                ) : (
                  <FontAwesomeIcon icon={faUser} className="um-avatar" />
                )}
                <span className="um-name">{name}</span>
              </div>

              <div className="um-actions">
                {/* 🔁 Toggle superuser */}
                <button
                  className={
                    "um-superBtn " +
                    (user.is_superuser ? "is-danger" : "is-success")
                  }
                  onClick={() =>
                    handleToggleSuperuser(user.id, !user.is_superuser)
                  }
                  disabled={!isSuperUser}
                  title={!isSuperUser ? "Doar superuser" : ""}
                >
                  {user.is_superuser
                    ? "Dezactivare Superuser"
                    : "Activare Superuser"}
                </button>

                {/* 🗑️ Delete */}
                <button
                  type="button"
                  className="sr-servicii__iconBtn sr-servicii__iconBtn--danger"
                  onClick={() => handleDeleteUser(user.id)}
                  disabled={!isSuperUser}
                  title={isSuperUser ? "Șterge utilizator" : "Doar superuser"}
                  style={{ opacity: isSuperUser ? 1 : 0.4 }}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default UsersManager;